/**
 * Form-state Zod schemas for the GoodsShipment detail view.
 *
 * Two schema levels:
 * - liveSchema: validated onTouched (as user edits) — only critical fields required
 * - saveSchema: validated on save — all required fields enforced
 *
 * saveSchema extends liveSchema so save always includes live rules.
 *
 * @see LLD Section 4.2 — Zod Schemas
 * @see UX Mock: GoodsShipment component (Figma)
 */
import { z } from 'zod';
import { valuationAdjustmentEntrySchema } from './goodsItemDetail';
import { entityRefSchema } from './common';

export const transportDocSchema = z.object({
  type: z.string().nullish(),
  identification: z.string().nullish(),
});

export const supportingDocSchema = z.object({
  type: z.string().nullish(),
  identification: z.string().nullish(),
});

export const previousDocSchema = z.object({
  type: z.string().nullish(),
  identification: z.string().nullish(),
  category: z.string().nullish(),
  line: z.coerce.number().int().nullish(),
});

/** Validated live (onTouched) — critical fields only */
export const goodsShipmentLiveSchema = z.object({
  transportDoc: transportDocSchema,
  supportingDocs: z.array(supportingDocSchema),
  previousDocs: z.array(previousDocSchema),
  incoterms: z.string().min(1, 'Incoterms is required').nullish(),
  incotermPlace: z.string().nullish(),
  currency: z.string().nullish(),
  invoiceValue: z.number().nullish(),
  exportCountry: z.string().nullish(),
  importCountry: z.string().nullish(),
  adjustments: z.array(valuationAdjustmentEntrySchema),
  importer: entityRefSchema,
  buyer: entityRefSchema,
  consignee: entityRefSchema,
});

/** Validated on save — extends live with additional required fields */
export const goodsShipmentSaveSchema = goodsShipmentLiveSchema.extend({
  incoterms: z.string().min(1, 'Incoterms is required'),
  currency: z.string().min(1, 'Currency is required'),
});

/** Default form schema — use liveSchema for onTouched validation */
export const goodsShipmentFormSchema = goodsShipmentLiveSchema;

export type GoodsShipmentFormState = z.infer<typeof goodsShipmentLiveSchema>;
